import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import {
  Factory,
  Package,
  Clock,
  TrendingUp,
  AlertTriangle,
  ClipboardList,
  Users,
  Plus,
  ArrowRight,
  Hammer,
  Boxes,
  BarChart3,
  CheckCircle2,
  Timer,
  Calendar
} from "lucide-react";
import { usePermissions } from "../../hooks/usePermissions";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { handleApiError } from "../../utils/errorHandler";
import { useTranslation } from "../../contexts/I18nContext";

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const STATO_COMMESSA = {
  aperta: { label: "Aperta", color: "bg-blue-100 text-blue-800" },
  in_corso: { label: "In Corso", color: "bg-yellow-100 text-yellow-800" },
  sospesa: { label: "Sospesa", color: "bg-orange-100 text-orange-800" },
  completata: { label: "Completata", color: "bg-green-100 text-green-800" },
  annullata: { label: "Annullata", color: "bg-red-100 text-red-800" }
};

export default function ProduzioneDashboardPage() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { canEdit } = usePermissions();
  const canEditProduzione = canEdit("produzione");
  
  const [stats, setStats] = useState(null);
  const [commesseRecenti, setCommesseRecenti] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, commesseRes] = await Promise.all([
        api.get("/tenant/produzione/stats"),
        api.get("/tenant/produzione/commesse?stato=in_corso")
      ]);
      setStats(statsRes.data);
      setCommesseRecenti(commesseRes.data.slice(0, 5));
    } catch (error) {
      
      handleApiError(error, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Prepara dati per grafici
  const commessePerStato = stats?.commesse_per_stato || {};
  const pieData = Object.entries(commessePerStato).map(([stato, data]) => ({
    name: STATO_COMMESSA[stato]?.label || stato,
    value: data.count
  }));

  const orePerTipo = stats?.ore_per_tipo || [];
  const barData = orePerTipo.slice(0, 6);

  return (
    <div className="space-y-6" data-testid="produzione-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">{t("production.dashboard")}</h1>
          <p className="text-muted-foreground">Panoramica attivita produttive e commesse</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate(`/${tenant}/produzione/cronoprogramma`)} data-testid="btn-cronoprogramma">
            <Factory className="h-4 w-4 mr-2" />
            Cronoprogramma
          </Button>
          <Button variant="outline" onClick={() => navigate(`/${tenant}/produzione/fornitori`)} data-testid="btn-fornitori">
            <Factory className="h-4 w-4 mr-2" />
            Fornitori
          </Button>
          <Button variant="outline" onClick={() => navigate(`/${tenant}/produzione/tipi-attivita`)} data-testid="btn-tipi-attivita">
            <Factory className="h-4 w-4 mr-2" />
            Tipologie Attività
          </Button>
          <Button variant="outline" onClick={() => navigate(`/${tenant}/produzione/stabilimenti`)}>
            <Factory className="h-4 w-4 mr-2" />
            Stabilimenti
          </Button>
          <Button onClick={() => navigate(`/${tenant}/produzione/commesse`)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuova Commessa
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
          onClick={() => navigate(`/${tenant}/produzione/commesse`)}
          data-testid="kpi-commesse"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="card-title-responsive">Commesse Totali</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </CardHeader>
          <CardContent className="card-content-responsive">
            <div className="stat-number">{stats?.totale_commesse || 0}</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs">
                {commessePerStato.in_corso?.count || 0} in corso
              </Badge>
              {stats?.commesse_in_ritardo > 0 && (
                <Badge variant="destructive" className="text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {stats.commesse_in_ritardo} in ritardo
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
          onClick={() => navigate(`/${tenant}/produzione/ore`)}
          data-testid="kpi-ore"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="card-title-responsive">Ore Lavorate</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </CardHeader>
          <CardContent className="card-content-responsive">
            <div className="stat-number">{stats?.ore_effettive_totali?.toFixed(0) || 0}h</div>
            <p className="card-subtitle-responsive text-muted-foreground mt-1">
              Budget: {stats?.ore_budget_totali?.toFixed(0) || 0}h
            </p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
          onClick={() => navigate(`/${tenant}/produzione/materiale`)}
          data-testid="kpi-materiale"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="card-title-responsive">Materiale</CardTitle>
            <Boxes className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </CardHeader>
          <CardContent className="card-content-responsive">
            <div className="stat-number">
              {stats?.materiale_ricevuto || 0}/{stats?.materiale_totale || 0}
            </div>
            <Progress 
              value={stats?.materiale_totale ? (stats.materiale_ricevuto / stats.materiale_totale) * 100 : 0} 
              className="h-2 mt-2"
            />
            <p className="card-subtitle-responsive text-muted-foreground mt-1">
              {stats?.materiale_totale - stats?.materiale_ricevuto || 0} in attesa
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden" data-testid="kpi-efficienza">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="card-title-responsive">Efficienza Globale</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </CardHeader>
          <CardContent className="card-content-responsive">
            <div className="stat-number">
              {stats?.efficienza_globale ? `${stats.efficienza_globale}%` : '-'}
            </div>
            <p className="card-subtitle-responsive text-muted-foreground mt-1">
              Lead time medio: {stats?.lead_time_medio_giorni || 0} giorni
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Grafici e Commesse Recenti */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Grafico Commesse per Stato */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Commesse per Stato</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                Nessuna commessa presente
              </div>
            )}
          </CardContent>
        </Card>

        {/* Grafico Ore per Tipo Lavorazione */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ore per Tipo Lavorazione</CardTitle>
          </CardHeader>
          <CardContent>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={barData}>
                  <XAxis dataKey="tipo" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="ore" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                Nessuna ora registrata
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Commesse In Corso */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Commesse In Corso</CardTitle>
            <CardDescription>Ultime commesse attive</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/${tenant}/produzione/commesse`)}>
            Vedi tutte <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {commesseRecenti.length > 0 ? (
            <div className="space-y-4">
              {commesseRecenti.map((commessa) => (
                <div
                  key={commessa.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                  onClick={() => navigate(`/${tenant}/produzione/commesse/${commessa.id}`)}
                  data-testid={`commessa-${commessa.codice}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{commessa.codice}</span>
                      <Badge className={STATO_COMMESSA[commessa.stato]?.color || "bg-gray-100"}>
                        {STATO_COMMESSA[commessa.stato]?.label || commessa.stato}
                      </Badge>
                      {commessa.priorita === 1 && (
                        <Badge variant="destructive" className="text-xs">Alta priorita</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{commessa.titolo}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      {commessa.cliente_nome && (
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {commessa.cliente_nome}
                        </span>
                      )}
                      {commessa.data_consegna_prevista && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Consegna: {new Date(commessa.data_consegna_prevista).toLocaleDateString('it-IT')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold">{commessa.avanzamento_percentuale?.toFixed(0) || 0}%</div>
                    <Progress 
                      value={commessa.avanzamento_percentuale || 0} 
                      className="w-24 h-2 mt-1"
                    />
                    <div className="text-xs text-muted-foreground mt-1">
                      {commessa.ore_effettive?.toFixed(0) || 0}h / {commessa.ore_budget_totali?.toFixed(0) || 0}h
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>{t("production.noOrdersInProgress")}</p>
              <Button className="mt-4" onClick={() => navigate(`/${tenant}/produzione/commesse`)}>
                <Plus className="h-4 w-4 mr-2" />
                Crea Prima Commessa
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Azioni Rapide */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate(`/${tenant}/produzione/commesse`)}
        >
          <ClipboardList className="h-6 w-6" />
          <span>Gestione Commesse</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate(`/${tenant}/produzione/materiale`)}
        >
          <Boxes className="h-6 w-6" />
          <span>Materiale</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate(`/${tenant}/produzione/ore`)}
        >
          <Timer className="h-6 w-6" />
          <span>Registra Ore</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate(`/${tenant}/produzione/stabilimenti`)}
        >
          <Factory className="h-6 w-6" />
          <span>Layout Stabilimenti</span>
        </Button>
      </div>
    </div>
  );
}
